import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { Languages } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  detectBrowserLanguage,
  getPathLanguage,
  stripLanguagePrefix,
  withLanguagePrefix,
} from "@/lib/i18n";

const ELIGIBLE_PATHS = new Set(["/", "/templates", "/pricing"]);
const DISMISSED_KEY = "creavideos-language-suggestion-dismissed";
const LEGACY_DISMISSED_KEYS = [...ELIGIBLE_PATHS].map(
  (path) => `${DISMISSED_KEY}:${path}`
);

const COPY = {
  en: {
    title: "Would you like to go to the English version?",
    description: "We noticed that your browser is set to English.",
    yes: "Yes",
    no: "No",
    dismiss: "Don't show this message again (you can change the language in the menu).",
    close: "Close language suggestion",
  },
  fr: {
    title: "Voulez-vous aller à la version française ?",
    description: "Nous avons remarqué que votre navigateur est configuré en français.",
    yes: "Oui",
    no: "Non",
    dismiss: "Ne plus montrer ce message (vous pouvez changer la langue dans le menu).",
    close: "Fermer la suggestion de langue",
  },
  es: {
    title: "¿Quieres ir a la versión en español?",
    description: "Hemos detectado que tu navegador está configurado en español.",
    yes: "Sí",
    no: "No",
    dismiss: "No volver a mostrar este mensaje (puedes cambiar el idioma en el menú).",
    close: "Cerrar la sugerencia de idioma",
  },
};

export default function LanguageSuggestionDialog() {
  const location = useLocation();
  const [suggestedLanguage, setSuggestedLanguage] = useState(null);
  const [dontShowAgain, setDontShowAgain] = useState(false);
  const basePath = stripLanguagePrefix(location.pathname);

  useEffect(() => {
    setDontShowAgain(false);

    if (!ELIGIBLE_PATHS.has(basePath)) {
      setSuggestedLanguage(null);
      return;
    }

    const browserLanguage = detectBrowserLanguage();
    const pageLanguage = getPathLanguage(location.pathname);
    const wasDismissed =
      window.localStorage.getItem(DISMISSED_KEY) === "true" ||
      LEGACY_DISMISSED_KEYS.some(
        (key) => window.localStorage.getItem(key) === "true"
      );

    // Carry forward opt-outs saved by the previous per-page implementation.
    if (wasDismissed) {
      window.localStorage.setItem(DISMISSED_KEY, "true");
    }

    setSuggestedLanguage(
      browserLanguage !== pageLanguage && !wasDismissed ? browserLanguage : null
    );
  }, [basePath, location.pathname]);

  const rememberPreference = () => {
    if (dontShowAgain) {
      window.localStorage.setItem(DISMISSED_KEY, "true");
    }
  };

  const stayOnPage = () => {
    rememberPreference();
    setSuggestedLanguage(null);
  };

  const switchLanguage = () => {
    rememberPreference();
    const destination = withLanguagePrefix(basePath, suggestedLanguage);
    window.location.assign(`${destination}${location.search}${location.hash}`);
  };

  if (!suggestedLanguage) return null;

  const copy = COPY[suggestedLanguage];

  return (
    <Dialog open onOpenChange={(open) => !open && stayOnPage()}>
      <DialogContent
        className="max-w-md rounded-2xl border-0 p-0 shadow-2xl"
        aria-label={copy.close}
        data-auto-translate="false"
      >
        <div className="rounded-t-2xl bg-gradient-to-br from-teal-500 to-cyan-600 px-6 py-7 text-white">
          <Languages className="mb-4 h-9 w-9" aria-hidden="true" />
          <DialogHeader>
            <DialogTitle className="pr-7 text-2xl leading-tight">{copy.title}</DialogTitle>
            <DialogDescription className="pt-1 text-sm text-teal-50">
              {copy.description}
            </DialogDescription>
          </DialogHeader>
        </div>

        <div className="space-y-6 rounded-b-2xl bg-slate-100 bg-opacity-100 px-6 py-6">
          <DialogFooter className="grid grid-cols-2 gap-3 sm:space-x-0">
            <Button type="button" variant="outline" onClick={stayOnPage}>
              {copy.no}
            </Button>
            <Button type="button" className="btn-primary" onClick={switchLanguage}>
              {copy.yes}
            </Button>
          </DialogFooter>

          <label className="flex cursor-pointer items-start gap-3 text-sm leading-5 text-slate-600">
            <Checkbox
              checked={dontShowAgain}
              onCheckedChange={(checked) => setDontShowAgain(checked === true)}
              className="mt-0.5"
            />
            <span>{copy.dismiss}</span>
          </label>
        </div>
      </DialogContent>
    </Dialog>
  );
}
